import {ColumnDef, FilterDef, FilterOperator, FilterType, IdModelTable} from "../../../components/ExTable";
import {useTranslation} from "react-i18next";
import React from "react";
import {dateTimeColumnDef} from "../../../components/ExTable/variables";
import {IdListRequest} from "../../../app/api/types";
import {
    DeviceJob,
    DeviceJobField,
    DeviceJobPriority,
    DeviceJobSource,
    DeviceJobStatus,
    DeviceJobType,
} from "../../../app/api/model/deviceJob";
import {useOrganizationDeviceJobListQuery, useStageListQuery} from "../../../app/api/api";
import {getTranslation, getTranslationKey, TranslationContext} from "../../../utils/getTranslationKey";
import {EMPTY_UI} from "../../../variables";
import {ordinaryDetailPageLink} from "../../../utils/ordinaryDetailPageLink";
import {RoutePath} from "../../../types";

export default function DeviceJobList({organizationId}: {organizationId: number}) {
    const {t} = useTranslation();
    const {data: stageListResponse} = useStageListQuery({});

    const columnDefs: ColumnDef<DeviceJob>[] = [
        {
            fieldName: DeviceJobField.ID,
            label: t("generic_id"),
            sortable: true,
            width: 150,
            minWidth: 150,
            sticky: true,
        },
        {
            fieldName: DeviceJobField.REQUESTED_AT,
            label: t("deviceJob_requestedAt"),
            ...dateTimeColumnDef,
            sortable: true,
            width: 150,
            minWidth: 150,
        },
        {
            fieldName: DeviceJobField.SOURCE,
            label: t("deviceJob_requestType"),
            sortable: true,
            formatCellText: ({row}) =>
                getTranslation({context: TranslationContext.DEVICE_JOB_SOURCE, value: row[DeviceJobField.SOURCE], t}),
            width: 150,
            minWidth: 150,
        },
        {
            fieldName: DeviceJobField.TYPE,
            label: t("deviceJob_type"),
            sortable: true,
            formatCellText: ({row}) =>
                getTranslation({context: TranslationContext.DEVICE_JOB_TYPE, value: row[DeviceJobField.TYPE], t}),
            width: 150,
            minWidth: 150,
        },
        {
            fieldName: DeviceJobField.ENVIRONMENT,
            label: t("deviceJob_targetEnvironment"),
            sortable: true,
            formatCellText: ({row}) => {
                return (
                    stageListResponse?.payload?.find((s) => s.identifier === row[DeviceJobField.ENVIRONMENT])
                        ?.description ||
                    row[DeviceJobField.ENVIRONMENT] ||
                    EMPTY_UI
                );
            },
            width: 200,
        },
        {
            fieldName: DeviceJobField.VEHICLE,
            label: t("deviceJob_vehicle"),
            sortable: true,
            width: 120,
        },
        {
            fieldName: DeviceJobField.DEVICE_ID,
            label: t("deviceJob_deviceId"),
            generateLink: (row) => ordinaryDetailPageLink(RoutePath.DEVICES, {id: row[DeviceJobField.DEVICE_ID]}),
            sortable: true,
            width: 120,
        },
        {
            fieldName: DeviceJobField.COMPANY_CARD_NUMBER,
            label: t("deviceJob_companyCardNo"),
            sortable: true,
            width: 180,
        },
        {
            fieldName: DeviceJobField.DRIVER_CARD_NUMBER,
            label: t("deviceJob_driverCardNo"),
            sortable: true,
            width: 200,
        },
        {
            fieldName: DeviceJobField.PERIOD_START,
            label: t("deviceJob_periodStart"),
            ...dateTimeColumnDef,
            sortable: false,
            width: 150,
        },
        {
            fieldName: DeviceJobField.PERIOD_END,
            label: t("deviceJob_periodEnd"),
            ...dateTimeColumnDef,
            sortable: false,
            width: 150,
        },
        {
            fieldName: DeviceJobField.STATUS,
            label: t("deviceJob_status"),
            sortable: true,
            formatCellText: ({row}) =>
                getTranslation({context: TranslationContext.DEVICE_JOB_STATUS, value: row[DeviceJobField.STATUS], t}),
            generateLink: (row: DeviceJob) =>
                `/${RoutePath.DEVICES}/${row[DeviceJobField.DEVICE_ID]}?&filter=[{"f":"jobID","o":"eq","v":${
                    row.id || 0
                }}]#1`,
            width: 150,
        },
        {
            fieldName: DeviceJobField.RETRIES,
            label: t("deviceJob_retries"),
            sortable: true,
            width: 110,
        },
        {
            fieldName: DeviceJobField.USER,
            label: t("deviceJob_user"),
            sortable: true,
            width: 200,
        },
        {
            fieldName: DeviceJobField.PRIORITY,
            label: t("deviceJob_priority"),
            formatCellText: ({row}) =>
                getTranslation({
                    context: TranslationContext.DEVICE_JOB_PRIORITY,
                    value: row[DeviceJobField.PRIORITY],
                    t,
                }),
            sortable: true,
            width: 100,
        },
        {
            fieldName: DeviceJobField.COMPLETED_AT,
            label: t("deviceJob_completedAt"),
            ...dateTimeColumnDef,
            sortable: true,
            width: 150,
        },
    ];

    const filterDefs: FilterDef<DeviceJob>[] = [
        {
            fieldName: DeviceJobField.REQUESTED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: DeviceJobField.ID,
            label: t("deviceHistoryjobID"),
            type: FilterType.NUMBER,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
        },
        {
            fieldName: DeviceJobField.SOURCE,
            filterOperators: [FilterOperator.IN],
            type: FilterType.MULTI_CHOICE_ENUM,
            enumValues: Object.keys(DeviceJobSource).map((type) => {
                return {
                    value: type,
                    labelKey: getTranslationKey({context: TranslationContext.DEVICE_JOB_SOURCE, value: type}),
                };
            }),
        },
        {
            fieldName: DeviceJobField.TYPE,
            filterOperators: [FilterOperator.IN],
            type: FilterType.MULTI_CHOICE_ENUM,
            enumValues: Object.keys(DeviceJobType).map((type) => {
                return {
                    value: type,
                    labelKey: getTranslationKey({context: TranslationContext.DEVICE_JOB_TYPE, value: type}),
                };
            }),
        },
        {
            fieldName: DeviceJobField.ENVIRONMENT,
            filterOperators: [FilterOperator.IN],
            type: FilterType.MULTI_CHOICE_ENUM,
            enumValues:
                stageListResponse?.payload?.map((s) => {
                    return {value: s.identifier, labelKey: s.description};
                }) ?? [],
        },
        {
            fieldName: DeviceJobField.VEHICLE,
            filterOperators: [FilterOperator.EQ, FilterOperator.CONTAINS, FilterOperator.IN],
        },
        {
            fieldName: DeviceJobField.DEVICE_ID,
            filterOperators: [FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: DeviceJobField.COMPANY_CARD_NUMBER,
            filterOperators: [FilterOperator.EQ, FilterOperator.CONTAINS, FilterOperator.IN],
        },
        {
            fieldName: DeviceJobField.DRIVER_CARD_NUMBER,
            filterOperators: [FilterOperator.EQ, FilterOperator.CONTAINS, FilterOperator.IN],
        },
        {
            fieldName: DeviceJobField.STATUS,
            filterOperators: [FilterOperator.IN],
            type: FilterType.MULTI_CHOICE_ENUM,
            enumValues: Object.keys(DeviceJobStatus).map((type) => {
                return {
                    value: type,
                    labelKey: getTranslationKey({context: TranslationContext.DEVICE_JOB_STATUS, value: type}),
                };
            }),
        },
        {
            fieldName: DeviceJobField.RETRIES,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
        },
        {
            fieldName: DeviceJobField.PRIORITY,
            filterOperators: [FilterOperator.IN],
            type: FilterType.MULTI_CHOICE_ENUM,
            enumValues: Object.keys(DeviceJobPriority).map((type) => {
                return {
                    value: type,
                    labelKey: getTranslationKey({context: TranslationContext.DEVICE_JOB_PRIORITY, value: type}),
                };
            }),
        },
        {
            fieldName: DeviceJobField.COMPLETED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
    ];

    return (
        <>
            <IdModelTable<DeviceJob, IdListRequest>
                queryHook={useOrganizationDeviceJobListQuery}
                additionalRequestParams={{id: organizationId}}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("organizations_deviceJobList")}
                selectableRows={false}
                compact={true}
            />
        </>
    );
}
